import React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  FormLabel,
  TextareaAutosize,
  Alert,
  Checkbox,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import FileUpload from "./FileUploadBox.tsx";
import { FieldError } from "../ScoreCardValidation/ValidationErrors.tsx";
import type { ValidationError } from "../ScoreCardValidation/ValidationSchema.tsx";

const FileUploadBox = styled(Box)(({ theme }) => ({
  border: "1px dashed",
  borderColor: theme.palette.divider,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
}));

const UploadIconBox = styled(Box)(({ theme }) => ({
  width: "48px",
  height: "48px",
  border: "1px solid",
  borderColor: theme.palette.divider,
  borderRadius: theme.shape.borderRadius,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: theme.spacing(1),
}));

interface QuestionItemProps {
  questionsData: any;
  question: any;
  index: number;
  answers: Record<string, any>;
  setAnswer: any;
  setUploadedFiles: any;
  uploadedFiles: any;
  onAnswerChange: (questionId: any, value: any) => void;
  onCommentChange: (questionId: any, comment: string) => void;
  onFileUpload: (
    event: React.ChangeEvent<HTMLInputElement>,
    section: string
  ) => void;
  onRemoveFile: (section: string, index: number) => void;
  validationErrors?: ValidationError[];
}

// Add a validation function to check if the required number of images are uploaded
const validateImageUploads = (
  optionImage: number,
  uploadedFiles: string[] | undefined
): boolean => {
  if (optionImage <= 0) return true; // No images required
  if (!uploadedFiles || uploadedFiles.length < optionImage) return false;
  return true;
};

// Add this inside the QuestionItem component, after the options parsing
const getImageValidationMessage = (optionImage: number): string => {
  return `This answer requires minimum ${optionImage} image${
    optionImage > 1 ? "s" : ""
  }.`;
};

const validateTextFieldCharacter = (question: any) => {
  if (question.minChar <= 0) return true;
};

const QuestionItem: React.FC<QuestionItemProps> = ({
  questionsData,
  question,
  index,
  answers,
  setAnswer,
  setUploadedFiles,
  uploadedFiles,
  onAnswerChange,
  onCommentChange,
  onFileUpload,
  onRemoveFile,
  validationErrors = [],
}) => {
  console.log(uploadedFiles);
  console.log(answers);
  // State to store the full response object for each question
  const [responseData, setResponseData] = useState<any>({
    questionId: question.questionId,
    reportDtlId: question.reportDtlId,
    value: answers[question.questionId]?.value || "",
    photoUrl: answers[question.questionId]?.photoUrl || "",
    score: Math.round(
      answers[question.questionId]?.score || question.score || 0
    ),
    comment: answers[question.questionId]?.comment || "",
    isAnswered: false, // Default to false
    optionImage:
      JSON.parse(question.qOptionJson || "[]").find(
        (opt: any) =>
          opt.value === (answers[question.questionId]?.value || question.value)
      )?.optionImage || 0,
  });
  console.log(responseData);

  useEffect(() => {
    // Only initialize if the files aren't already in the state
    if (
      !uploadedFiles.questionFiles[question.questionId] ||
      uploadedFiles.questionFiles[question.questionId].length === 0
    ) {
      const photoUrlToUse =
        answers[question.questionId]?.photoUrl || question.photoUrl || "";
      const fileArray = photoUrlToUse
        .split(",")
        .map((str) => str.trim())
        .filter(Boolean);

      if (fileArray.length > 0) {
        setUploadedFiles((prevState: any) => ({
          ...prevState,
          questionFiles: {
            ...prevState.questionFiles,
            [question.questionId]: fileArray,
          },
        }));

        // Also update the answers state to ensure consistency
        if (!answers[question.questionId]?.photoUrl && fileArray.length > 0) {
          onAnswerChange(question.questionId, {
            reportDtlId: question.reportDtlId,
            value: responseData.value,
            photoUrl: fileArray.join(","),
            score: responseData.score,
            comment: responseData.comment || question.comment || "",
          });
        }
      }
    }
  }, [question.photoUrl, answers[question.questionId]?.photoUrl]);
  // setUploadedFiles((prevState: any) => {
  //   const fileArray = question.photoUrl
  //     ?.split(",")
  //     .map((str) => str.trim())
  //     .filter(Boolean) || [];

  //   return {
  //     ...prevState,
  //     questionFiles: {
  //       ...prevState.questionFiles,
  //       [question.questionId]: fileArray,
  //     },
  //   };
  // });

  // useEffect(() => {
  //   if (questionsData) {
  //     setAnswer(
  //       questionsData.questions.reduce((acc, question) => {
  //         return {
  //           ...acc,
  //           [question.questionId]: {
  //             reportDtlId: question.reportDtlId,
  //             value: question.value || "",
  //             photoUrl: question.photoUrl || "",
  //             score: question.score || 0,
  //             comment: question.comment || "",
  //             isAnswered: question.isAnswered || false,
  //           },
  //         };
  //       }, {})
  //     );
  //   }
  // }, [questionsData]);

  useEffect(() => {
    // Update isAnswered based on the presence of a comment
    const currentFiles = uploadedFiles.questionFiles[question.questionId] || [];
    const isImageValid = validateImageUploads(
      responseData.optionImage,
      currentFiles
    );

    setResponseData((prevData) => ({
      ...prevData,
      photoUrl: answers[question.questionId]?.photoUrl,
      isAnswered: prevData.comment.length > 0,
      isImageValid: isImageValid,
    }));

    // Update the answer with the validation status
    if (responseData.value) {
      onAnswerChange(question.questionId, {
        ...answers[question.questionId],
        isImageValid: isImageValid,
      });
    }
  }, [
    uploadedFiles,
    question.questionId,
    responseData.comment,
    responseData.optionImage,
  ]);

  // Handle change in comment
  const handleCommentChange = (newComment: string) => {
    setResponseData((prevData) => ({
      ...prevData,
      comment: newComment,
    }));

    onAnswerChange(question.questionId, {
      reportDtlId: question.reportDtlId,
      value: responseData.value,
      photoUrl: responseData.photoUrl,
      score: responseData.score,
      comment: newComment,
      isAnswered: newComment.length > 0,
    });
  };

  // Handle changes in other fields (like radio button selection)
  const handleFieldChange = (newValue: string) => {
    const selectedOption = options.find((opt: any) => opt.value === newValue);

    if (selectedOption) {
      const optionImage = selectedOption.optionImage || 0;
      const currentUploadedFiles =
        uploadedFiles.questionFiles[question.questionId] || [];
      const isImageValid = validateImageUploads(
        optionImage,
        currentUploadedFiles
      );

      setResponseData((prevData) => ({
        ...prevData,
        value: selectedOption.value,
        score: selectedOption.optionScore || 0,
        optionImage: optionImage,
        isImageValid: isImageValid,
      }));

      onAnswerChange(question.questionId, {
        reportDtlId: question.reportDtlId,
        value: selectedOption.value,
        photoUrl: responseData.photoUrl,
        score: selectedOption.optionScore || 0,
        comment: responseData.comment,
        isAnswered: true,
        optionImage: optionImage,
        isImageValid: isImageValid,
      });
    }
  };

  let bgColor = "#e8f5e9"; // Default green
  if (question.score > 0) {
    bgColor = "#fff9c4"; // Yellow for scoring questions
  }
  if (question.qType === "T") {
    bgColor = "#f8bbd0"; // Pink for text questions
  }

  let options: any = [];
  try {
    options = JSON.parse(question.qOptionJson || "[]"); // Safely parse JSON
    if (question.questionId === 51) {
      console.log(options);
    }
  } catch (error) {
    console.error("Invalid JSON in question.qOptionJson:", error);
  }

  return (
    <Paper
      component="div"
      sx={{
        backgroundColor: "transparent",
        p: 3,
        borderRadius: "10px",
        mb: 2,
        border: "1px solid #eee",
      }}
      data-field={`question_${question.questionId}`}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          alignItems: "flex-start",
        }}>
        {/* <Typography
          variant="subtitle1"
          dangerouslySetInnerHTML={{
            __html: `${index + 1}. ${question.questions}`,
          }}
          sx={{
            display: "flex",
            fontWeight: "bold",
            fontSize: "15px",
            color: "#333",
            maxWidth: "75%",
          }}
        /> */}

        <Box
          sx={{ display: "flex", alignItems: "flex-start", maxWidth: "75%" }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "bold",
              fontSize: "15px",
              color: "#333",
              mr: 1,
            }}>
            {index + 1}.
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: "15px",
              fontWeight: "bold",
              color: "#333",
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
              flex: 1,
            }}>
            <span dangerouslySetInnerHTML={{ __html: question.questions }} />
          </Typography>
        </Box>

        {/* Score display box */}
        {/* {question.maxScore === 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#e8e8d8", // Light grayish-green color
              color: "#a8a890", // Muted green text color
              padding: "8px 16px",
              borderRadius: "4px",
              width: "100px",
              height: "auto",
            }}
          >
            <Typography variant="body2" fontWeight="500" sx={{ fontSize: "14px" }}>
              Non Scoring
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#666",
                fontSize: "11px",
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              Add Score
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                backgroundColor: "#e8e8d8", // Light grayish-green color
                borderRadius: "4px",
                padding: "4px",
                width: "100px",
              }}
            >
              {question.qType === "T" ? (
                <TextField
                  variant="outlined"
                  size="small"
                  value={responseData.score}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, "")
                    if (value.length > 4) value = value.slice(0, 4)

                    setResponseData((prevData) => ({
                      ...prevData,
                      score: value,
                    }))

                    onAnswerChange(question.questionId, {
                      reportDtlId: question.reportDtlId,
                      value: responseData.value,
                      photoUrl: responseData.photoUrl,
                      score: value,
                      comment: responseData.comment,
                      isAnswered: true,
                    })
                  }}
                  sx={{
                    width: "100%",
                    backgroundColor: "#fff",
                    "& .MuiOutlinedInput-root": {
                      height: "36px",
                      "& fieldset": {
                        borderColor: "#ddd",
                        borderRadius: "4px",
                      },
                    },
                    "& .MuiInputBase-input": {
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "18px",
                      padding: "4px 8px",
                      color: "#333",
                    },
                  }}
                  inputProps={{
                    type: "number",
                    min: 0,
                    maxLength: 4,
                  }}
                />
              ) : (
                <TextField
                  variant="outlined"
                  size="small"
                  value={responseData.score}
                  disabled
                  sx={{
                    width: "100%",
                    backgroundColor: "#fff",
                    "& .MuiOutlinedInput-root": {
                      height: "36px",
                      "& fieldset": {
                        borderColor: "#ddd",
                        borderRadius: "4px",
                      },
                    },
                    "& .MuiInputBase-input": {
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "18px",
                      padding: "4px 8px",
                      color: "#333",
                    },
                  }}
                  inputProps={{
                    type: "number",
                  }}
                />
              )}
              <Typography variant="caption" sx={{ color: "#666", fontSize: "11px", mt: 0.5 }}>
                out of {question.maxScore}
              </Typography>
            </Box>
          </Box>
        )} */}
      </Box>
      {/* Unified Score display box */}

      <div className="scorecardContent" style={{ float: "left", width: "80%" }}>
        {question.questionDescription?.trim().length > 0 && (
          <Box
            sx={{
              mb: 2,
              backgroundColor: "#fefbea", // light background for contrast
              borderLeft: "4px solid #a29b66", // visual cue
              padding: "8px 12px",
              borderRadius: "4px",
              fontSize: "14px",
              color: "#001e78", // default text color matching the inline styles
            }}
            dangerouslySetInnerHTML={{ __html: question.questionDescription }}
          />
        )}
        {question.questionHintImages?.trim().length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              mb: 2,
              mt: -1, // pull closer to the description if needed
            }}>
            {question.questionHintImages
              .split(",")
              .map((src: string, index: number) => (
                <img
                  key={index}
                  src={src.trim()}
                  alt={`Hint ${index + 1}`}
                  style={{
                    maxHeight: "120px",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                  }}
                />
              ))}
          </Box>
        )}

        {question.questionHint?.trim().length > 0 && (
          <Box display="flex" alignItems="center" mb={2}>
            <Box
              sx={{
                backgroundColor: "#a29b66", // Olive background
                color: "white",
                fontWeight: "bold",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "0.875rem",
                marginRight: "8px",
              }}>
              Hint:
            </Box>

            <Typography
              sx={{
                backgroundColor: "#ffffff",
                borderRadius: "4px",
                padding: "4px 8px",
              }}
              dangerouslySetInnerHTML={{ __html: question.questionHint }}
              variant="body1"
            />
          </Box>
        )}

        {/* Radio buttons for question type R */}
        {question.qType === "R" && (
          <FormControl
            component="fieldset"
            error={validationErrors.some(
              (e) => e.field === `question_${question.questionId}`
            )}
            sx={{ width: "100%" }}>
            <RadioGroup
              row
              value={responseData.value || ""}
              onChange={(e) => handleFieldChange(e.target.value)}
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 1,
                "& .MuiFormControlLabel-root": {
                  margin: 0,
                  backgroundColor: "#fff",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  mb: 1,
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                },
                "& .Mui-checked + .MuiFormControlLabel-label": {
                  fontWeight: "bold",
                },
              }}>
              {options.map((option: any) => (
                <FormControlLabel
                  key={option.optionId}
                  value={option.value}
                  control={
                    <Radio
                      sx={{
                        color: "#aaa",
                        "&.Mui-checked": {
                          color: "#ADB792",
                        },
                      }}
                    />
                  }
                  label={
                    <span
                      dangerouslySetInnerHTML={{
                        __html: `${option.value?.replace(/<\/?p>/g, "")}
                      ${
                        option.optionScore
                          ? `(${option.optionScore} Points)`
                          : ""
                      }`,
                      }}
                      style={{ fontSize: "14px" }}
                    />
                  }
                />
              ))}
            </RadioGroup>
            <FieldError
              errors={validationErrors}
              field={`question_${question.questionId}`}
            />
          </FormControl>
        )}

        {/* Text area for question type T */}
        {question.qType === "T" && (
          <div>
            <TextareaAutosize
              minRows={3}
              placeholder="Write here..."
              value={responseData.value || ""}
              onChange={(e) => {
                const newValue = e.target.value;

                setResponseData((prevData) => ({
                  ...prevData,
                  value: newValue,
                }));

                onAnswerChange(question.questionId, {
                  reportDtlId: question.reportDtlId,
                  value: newValue,
                  photoUrl: responseData.photoUrl,
                  score: responseData.score,
                  comment: responseData.comment,
                  isAnswered: newValue.length > 0,
                });
              }}
              className={`textarea-custom ${
                validationErrors.some(
                  (e) => e.field === `question_${question.questionId}`
                )
                  ? "error-border"
                  : ""
              }`}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                fontSize: "14px",
                backgroundColor: "#fff",
                resize: "vertical",
                //minHeight: "100px",
                borderColor: validationErrors.some(
                  (e) => e.field === `question_${question.questionId}`
                )
                  ? "#d32f2f"
                  : "#ddd",
              }}
            />

            {/* Alert for minChar validation */}
            {question.minChar > 0 &&
              responseData.value?.length < question.minChar && (
                <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
                  Minimum {question.minChar} characters required.
                </Alert>
              )}

            <FieldError
              errors={validationErrors}
              field={`question_${question.questionId}`}
            />
          </div>
        )}

        {/* Rating scale for question type r */}
        {question.qType === "r" && (
          <FormControl
            component="fieldset"
            error={validationErrors.some(
              (e) => e.field === `question_${question.questionId}`
            )}
            sx={{ width: "100%", mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  p: 2,
                  border: "1px solid #ddd",
                }}>
                <RadioGroup
                  row
                  value={responseData.value || ""}
                  onChange={(e) => handleFieldChange(e.target.value)}
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}>
                  {options.map((option: any, idx: number) => (
                    <FormControlLabel
                      key={option.optionId}
                      value={option.value}
                      control={
                        <Radio
                          sx={{
                            color: "#aaa",
                            "&.Mui-checked": {
                              color: "#ADB792",
                            },
                            "& .MuiSvgIcon-root": {
                              fontSize: 20,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "500",
                            fontSize: "14px",
                            textAlign: "center",
                          }}>
                          {option.value}
                        </Typography>
                      }
                      labelPlacement="bottom"
                      sx={{
                        margin: 0,
                        alignItems: "center",
                        flexDirection: "column",
                      }}
                    />
                  ))}
                </RadioGroup>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                    px: 1,
                  }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#666",
                      fontSize: "12px",
                      maxWidth: "30%",
                    }}>
                    {options[0]?.radioRatingComment || "Would never Return"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#666",
                      fontSize: "12px",
                      textAlign: "center",
                    }}>
                    {options[4]?.radioRatingComment || "Neutral"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#666",
                      fontSize: "12px",
                      textAlign: "right",
                      maxWidth: "30%",
                    }}>
                    {options[9]?.radioRatingComment || "Amazing will Return"}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <FieldError
              errors={validationErrors}
              field={`question_${question.questionId}`}
            />
          </FormControl>
        )}

        {/* Checkboxes for question type C */}
        {question.qType === "C" && (
          <FormControl
            component="fieldset"
            error={validationErrors.some(
              (e) => e.field === `question_${question.questionId}`
            )}
            sx={{ width: "100%" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                gap: 2,
                mt: 2,
              }}>
              {options.map((option: any) => (
                <Box
                  key={option.optionId}
                  sx={{
                    backgroundColor: "#fff",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={(responseData.value || "")
                          .split(",")
                          .includes(option.value)}
                        onChange={(e) => {
                          const currentValues = (responseData.value || "")
                            .split(",")
                            .filter(Boolean);
                          let newValues;
                          if (e.target.checked) {
                            newValues = [...currentValues, option.value];
                          } else {
                            newValues = currentValues.filter(
                              (val) => val !== option.value
                            );
                          }
                          const newValueString = newValues.join(",");

                          setResponseData((prevData) => ({
                            ...prevData,
                            value: newValueString,
                          }));

                          onAnswerChange(question.questionId, {
                            reportDtlId: question.reportDtlId,
                            value: newValueString,
                            photoUrl: responseData.photoUrl,
                            score: responseData.score,
                            comment: responseData.comment,
                            isAnswered: newValues.length > 0,
                          });
                        }}
                        sx={{
                          color: "#aaa",
                          "&.Mui-checked": {
                            color: "#ADB792",
                          },
                        }}
                      />
                    }
                    label={
                      <span
                        dangerouslySetInnerHTML={{
                          __html: `${option.value?.replace(/<\/?p>/g, "")} ${
                            option.optionScore
                              ? `(${option.optionScore} Points)`
                              : ""
                          }`,
                        }}
                        style={{ fontSize: "14px" }}
                      />
                    }
                    sx={{
                      margin: 0,
                      width: "100%",
                    }}
                  />
                </Box>
              ))}
            </Box>
            <FieldError
              errors={validationErrors}
              field={`question_${question.questionId}`}
            />
          </FormControl>
        )}

        {/* Comment section */}
        {question.qType === "R" &&
          (() => {
            const selectedOption: any = options.find(
              (opt: any) => opt.value === responseData.value
            );

            if (selectedOption?.optionComment?.toLowerCase() !== "yes")
              return null;

            const commentType = selectedOption.optionCommentType || "text";
            const commentLabel =
              selectedOption.optionCommentLabel || "Add your Comment";

            return (
              <Box sx={{ mt: 2 }}>
                {["text", "longText"].includes(commentType) && (
                  <>
                    <FormLabel
                      sx={{
                        fontSize: "14px",
                        color: "#666",
                        mb: 1,
                        display: "block",
                      }}>
                      {commentLabel}
                      <span style={{ color: "#D32F2F" }}> *</span>
                    </FormLabel>
                    <TextareaAutosize
                      data-field={`question_${question.questionId}_comment`}
                      minRows={3}
                      placeholder="Write here..."
                      value={responseData.comment}
                      onChange={(e) => handleCommentChange(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                        fontSize: "14px",
                        backgroundColor: "#fff",
                        resize: "vertical",
                        borderColor: validationErrors.some(
                          (e) => e.field === `comment_${question.questionId}`
                        )
                          ? "#d32f2f"
                          : "#ddd",
                      }}
                    />
                  </>
                )}

                {commentType === "time" && (
                  <>
                    <FormLabel
                      sx={{
                        fontSize: "14px",
                        color: "#666",
                        mb: 1,
                        display: "block",
                      }}>
                      {commentLabel}
                      <span style={{ color: "#D32F2F" }}> *</span>
                    </FormLabel>
                    <TextField
                      type="time"
                      fullWidth
                      value={responseData.comment || ""}
                      onChange={(e) => handleCommentChange(e.target.value)}
                      error={validationErrors.some(
                        (e) => e.field === `comment_${question.questionId}`
                      )}
                      inputProps={{
                        step: 300, // 5-minute intervals
                      }}
                    />
                  </>
                )}

                {commentType === "checkbox" && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={responseData.comment === "true"}
                        onChange={(e) =>
                          handleCommentChange(
                            e.target.checked ? "true" : "false"
                          )
                        }
                        sx={{
                          color: "#aaa",
                          "&.Mui-checked": {
                            color: "#ADB792",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: "14px" }}>
                        {commentLabel}
                        <span style={{ color: "#D32F2F" }}> *</span>
                      </Typography>
                    }
                  />
                )}

                <FieldError
                  errors={validationErrors}
                  field={`comment_${question.questionId}`}
                />
              </Box>
            );
          })()}

        {/* Image upload validation message */}
        {responseData.optionImage > 0 &&
          !validateImageUploads(
            responseData.optionImage,
            uploadedFiles.questionFiles[question.questionId]
          ) && (
            <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
              {getImageValidationMessage(responseData.optionImage)}
            </Alert>
          )}

        {/* Photo upload section */}
        {(responseData.optionImage > 0 ||
          question.isPhotoRequire === 1 ||
          responseData.photoUrl !== "") && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: "#666",
                mb: 1,
                fontSize: "14px",
              }}>
              Upload Images{" "}
              {responseData.optionImage > 0
                ? `(minimum ${responseData.optionImage} images required)*`
                : ""}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <FileUpload
                section="questionFiles"
                title=""
                questionId={question.questionId}
                reportDetailId={question.reportDtlId}
                files={
                  (answers[question.questionId]?.photoUrl || "")
                    .split(",")
                    .map((str) => str.trim())
                    .filter(Boolean) ||
                  (question.photoUrl || "")
                    .split(",")
                    .map((str) => str.trim())
                    .filter(Boolean) ||
                  []
                }
                onUpload={onFileUpload}
                onRemove={onRemoveFile}
              />
            </Box>
          </Box>
        )}
      </div>
      <div className="scoreDisplay" style={{ float: "right" }}>
        <Box
          sx={{
            width: 120,
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          }}>
          {question.maxScore === 0 ? (
            // === Non-Scoring Box ===
            <Box
              sx={{
                backgroundColor: "#D4DBC2",
                width: "100%",
                height: "100%",
                minHeight: "100px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "12px",
              }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#a0aa8b",
                  textAlign: "center",
                  lineHeight: 1.3,
                }}>
                Non
                <br />
                Scoring
              </Typography>
            </Box>
          ) : (
            // === Scoring Box ===
            <>
              {/* Top Section */}
              <Box
                sx={{
                  backgroundColor: "#D4DBC2",
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "left",
                }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#000",
                    mb: 0.5,
                  }}>
                  Add Score
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  value={responseData.score}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, ""); // remove non-digit input
                    if (value.length > 4) value = value.slice(0, 4);

                    const numericValue = parseInt(value || "0", 10);
                    const clampedValue =
                      numericValue > question.maxScore
                        ? question.maxScore
                        : numericValue;

                    setResponseData((prevData) => ({
                      ...prevData,
                      score: clampedValue.toString(),
                    }));

                    onAnswerChange(question.questionId, {
                      reportDtlId: question.reportDtlId,
                      value: responseData.value,
                      photoUrl: responseData.photoUrl,
                      score: clampedValue.toString(),
                      comment: responseData.comment,
                      isAnswered: true,
                    });
                  }}
                  //disabled={question.qType !== "T"}
                  inputProps={{
                    type: "number",
                    min: 0,
                    max: question.maxScore,
                    style: {
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "18px",
                      color: "#000",
                      padding: "6px 8px",
                    },
                  }}
                  sx={{
                    width: "100%",
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-root": {
                      height: "40px",
                      "& fieldset": {
                        borderColor: "#ccc",
                        borderRadius: "8px",
                      },
                    },
                  }}
                />
              </Box>

              {/* Divider */}
              <Box sx={{ height: "2px", backgroundColor: "#fff" }} />

              {/* Bottom Section */}
              <Box
                sx={{
                  backgroundColor: "#D4DBC2",
                  textAlign: "center",
                  py: 0.75,
                }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#000",
                  }}>
                  out of {question.maxScore}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </div>
    </Paper>
  );
};

export default QuestionItem;
